import { CircularProgress } from '@civilgrid/ui/components';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import { addSingleFile as apiAddSingleFile, getSupportingFiles as apiGetSupportingFiles } from '~/api/civilsearch';
import { constructFile } from '~/common/miscUtils';
import { type CivilGridFile, FileCategory } from '~/common/types';
import UploadButton from '~/components2/forms/UploadButton';
import { Notify } from '~/components2/Notify';
import CivilSearchTable from '~/pages/admin/civilsearch/common/CivilSearchTable';
import store from '../../store';
import { columns } from './columns';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    refreshData?: () => void;
  }
}

export default function SupportingFiles() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [data, setData] = useState<CivilGridFile[]>([]);
  const { project } = useSnapshot(store) as typeof store;

  const fileData = useAsyncRetry(async () => {
    const files = await apiGetSupportingFiles(project!.id);
    setData([...files]);
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    initialState: {
      sorting: [{ id: 'createdAt', desc: false }],
    },
    meta: { refreshData: fileData.retry },
  });

  const onUpload = async (files: File[]) => {
    try {
      setIsProcessing(true);

      const uploads = files.map(async (f) => {
        const data = constructFile(f, {
          category: FileCategory.SupportingFile,
        });
        const file = await apiAddSingleFile(project!.id, data);
        return file;
      });

      await Promise.all(uploads);

      fileData.retry();
    } catch {
      Notify.error({ message: 'Error uploading files.' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (fileData.loading) return <CircularProgress indeterminate />;
  if (fileData.error) {
    return <div>An error occurred while fetching files.</div>;
  }

  return (
    <div>
      <CivilSearchTable id="supportingFiles" table={table}>
        <UploadButton isProcessing={isProcessing} disabled={isProcessing} multiple onUpload={onUpload}>
          Upload Files
        </UploadButton>
      </CivilSearchTable>
    </div>
  );
}
